import type { Messages } from './messages';

/**
 * Copy for `src/app/permissions/`. Owned by that page's author, and by nobody else.
 *
 * Every key starts with `permissions.` — the compiler refuses anything else, so
 * this file cannot collide with another page's. Shared words (save, cancel,
 * retry, statuses, role names) are already in `common.ts` and
 * `vocabulary.ts`; use those rather than restating them here.
 *
 * The *meaning* of each permission and role is not here: it is the server's
 * own sentence (`description.ar`), so the panel cannot drift from the code
 * that enforces it.
 */
export const permissions = {
  'permissions.title': 'الصلاحيات',
  'permissions.subtitle': 'ما يملكه كل دور من أدوار المنصّة، وما تحمله أدوار المنشآت افتراضيًا. للاطلاع فقط.',

  /* The one sentence (brief §2.5). */
  'permissions.boundaryTitle': 'ما لا يملكه أيّ دور',
  'permissions.boundary':
    'لا يملك أيّ دور في المنصّة، ولا أيّ صلاحية يمكن منحها لمشغّل، الاطلاع على محتوى منشأة: محادثات أعضائها وقضاياهم ومشاريعهم ومستنداتهم.',

  /* Tabs */
  'permissions.tabs': 'أقسام الصلاحيات',
  'permissions.tab.operator': 'صلاحيات المنصّة',
  'permissions.tab.tenant': 'صلاحيات المنشآت',

  /* (a) operator */
  'permissions.operatorRoles': 'أدوار مشغّلي المنصّة',
  'permissions.operatorRolesHint': 'الدور يُسند لكل مشغّل عند إنشاء حسابه، ولا يُعدَّل ما يحمله من هذه اللوحة.',
  'permissions.operatorMatrix': 'ما يملكه كل دور',
  'permissions.operatorMatrixHint': 'كل صف صلاحية ومعناها كما يطبّقها الخادم؛ وكل عمود دور.',
  'permissions.operatorMatrixCaption': 'صلاحيات المنصّة حسب دور المشغّل',
  'permissions.yourRole': 'دورك',

  /* (b) tenant */
  'permissions.tenantRoles': 'الأدوار الافتراضية في المنشآت',
  'permissions.tenantRolesHint':
    'هذا ما يحمله كل دور عند إنشاء المنشأة. قد تكون المنشأة عدّلت أدوارها بعد ذلك، فاعرض أدوارها هي لمعرفة الواقع.',
  'permissions.tenantMatrix': 'كتالوج صلاحيات المنشآت',
  'permissions.tenantMatrixHint': 'مرتّبة حسب المجموعة. الأعمدة هي الأدوار الافتراضية.',
  'permissions.tenantMatrixCaption': 'صلاحيات المنشآت حسب الدور الافتراضي',
  'permissions.directTitle': 'صلاحيات تُمنح لشخص لا لدور',
  'permissions.direct':
    'بعض الصلاحيات لا يحملها أيّ دور مهما عُدّل، لأنها تتيح الاطلاع على عمل الزملاء. تُمنح لعضو بعينه من تبويب «الأدوار والصلاحيات» في منشأته، وتُسحب منه هناك.',
  'permissions.directBadge': 'تُمنح لشخص',
  'permissions.directCell': 'لا يحملها أيّ دور — تُمنح لشخص مباشرة',

  /* Columns */
  'permissions.col.permission': 'الصلاحية',
  'permissions.col.role': 'الدور',
  'permissions.col.description': 'المعنى',
  'permissions.col.holds': 'عدد الصلاحيات',
  'permissions.holds': '{count} من {total}',

  /* Matrix marks (screen-reader text; the visible mark is a shape) */
  'permissions.granted': 'ممنوحة',
  'permissions.notGranted': 'غير ممنوحة',

  /* Filters */
  'permissions.search': 'ابحث في الصلاحيات',
  'permissions.searchPlaceholder': 'اسم الصلاحية أو رمزها أو معناها',
  'permissions.groupFilter': 'المجموعة',
  'permissions.allGroups': 'كل المجموعات',
  'permissions.roleFilter': 'حسب الدور الذي يحملها',
  'permissions.anyRole': 'كل الأدوار',
  'permissions.heldBy': 'يحملها {role}',
  'permissions.shown': '{count} من {total} صلاحية',
  'permissions.noMatch': 'لا صلاحية تطابق البحث',
  'permissions.noMatchBody': 'جرّب كلمة من المعنى أو رمز الصلاحية، أو امسح التصفية.',
  'permissions.empty': 'لم يُرجع الخادم أيّ صلاحية',
  'permissions.emptyBody': 'هذا غير متوقّع: الكتالوج جزء من شيفرة المنصّة. أعد المحاولة، وإن تكرّر فأبلغ فريق المنصّة.',

  /* Groups — `PermissionGroup` */
  'permissions.group.administration': 'إدارة المنشأة',
  'permissions.group.members': 'الأعضاء',
  'permissions.group.models': 'النماذج',
  'permissions.group.audit': 'التدقيق',
  'permissions.group.billing': 'التكاليف',
  'permissions.group.content': 'المحتوى الخاص',
  'permissions.group.knowledge': 'قاعدة المعرفة',
  'permissions.group.cases': 'القضايا',
  'permissions.group.review': 'المراجعة',

  /* Per-tenant picker */
  'permissions.perTenant': 'عرض لدى منشأة محددة',
  'permissions.perTenantFor': 'عرض لدى منشأة محددة — {role}',
  'permissions.perTenantNeeds': 'يتطلب صلاحية «{permission}»',
  'permissions.pickTitle': 'اختر منشأة',
  'permissions.pickBody': 'تنتقل إلى تبويب «الأدوار والصلاحيات» في المنشأة، حيث ترى ما يحمله دور «{role}» لديها فعلًا.',
} as const satisfies Messages<'permissions'>;
